import { Tags } from "lucide-react";
import { useEffect } from "react";

import { ESCAPE, topEscapePriority } from "../../components/escapeStack";
import { IconButton, type IconButtonSize } from "../../components/ui";
import { t } from "../../i18n/i18n";
import { type ControlKey, useUi } from "../../state/ui";
import { mapSurfaceVisible } from "../history";
import { isEditableTarget } from "../useHistoryKeys";
import { useDeployment } from "../units/useDeployment";
import { measureLevelOf } from "./measureView";

/** Bouton des noms des lignes et des zones de la ZO (docs/15 §4.2), dans la barre basse de la carte, après ↶ ↷ :
 *  il masque ou affiche les puces des lignes et les noms des zones, comme la case « Noms des lignes et des zones »
 *  des calques et la touche N — un seul état (`controlVisible.measureNames`), mémorisé par poste. Afficher les noms
 *  quand les lignes sont masquées rétablit aussi les lignes. Il ne touche ni les puces des LIMA (elles portent le
 *  GDH) ni les marques d'échelon. Absent sans lignes de la ZO (CENTAC, pas de fuseau). */

type Flags = Pick<Record<ControlKey, boolean>, "measures" | "measureNames">;

/** Noms affichés : la case des noms cochée et les lignes visibles. */
export function namesShown(flags: Flags): boolean {
  return flags.measureNames && flags.measures;
}

/** Changements d'état d'une bascule : noms affichés → masqués ; sinon noms affichés et lignes rétablies. */
export function toggledNames(flags: Flags): Partial<Flags> {
  return namesShown(flags) ? { measureNames: false } : { measureNames: true, measures: true };
}

/** Applique une bascule aux réglages des calques (état partagé, mémorisé par poste). */
export function toggleMeasureNames(): void {
  const ui = useUi.getState();
  const change = toggledNames(ui.controlVisible);
  for (const [key, value] of Object.entries(change) as [keyof Flags, boolean][]) ui.setControlVisible(key, value);
}

/** Case « Noms des lignes et des zones » des calques : cochée exactement quand le bouton est enfoncé (`namesShown`) ;
 *  la cocher ou la décocher fait la même bascule que le bouton (cocher rétablit aussi les lignes). */
export function setNamesShown(shown: boolean): void {
  if (shown !== namesShown(useUi.getState().controlVisible)) toggleMeasureNames();
}

type KeyLike = Pick<KeyboardEvent, "key" | "code" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey" | "repeat" | "target" | "defaultPrevented">;

const LATIN_LETTER = /^\p{Script=Latin}$/u;
const ANY_LETTER = /^\p{L}$/u;

/** L'appui est la touche N seule : par `e.key` (QWERTY, AZERTY…), ou par `e.code` si la disposition n'écrit pas en
 *  lettres latines (cyrillique, grec). Ignoré avec un modificateur (⌘, Ctrl, ⌥, ⇧), en répétition, pendant une saisie,
 *  pendant un tracé (`drawing`) ou quand un menu, une fenêtre ou une confirmation est ouvert. */
export function isNamesKey(e: KeyLike, drawing = false, overlay = topEscapePriority() >= ESCAPE.menu): boolean {
  if (e.defaultPrevented || e.repeat || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return false;
  const key = (e.key ?? "").toLowerCase();
  const letter = key === "n" || (ANY_LETTER.test(key) && !LATIN_LETTER.test(key) && e.code === "KeyN");
  if (!letter || drawing || overlay) return false;
  if (isEditableTarget(e.target)) return false;
  if (typeof document !== "undefined" && isEditableTarget(document.activeElement)) return false;
  return true;
}

export function MeasureNamesToggle({ size = "sm" }: { size?: IconButtonSize }) {
  const { data } = useDeployment();
  const has = measureLevelOf(data) !== null;
  const shown = useUi((s) => namesShown(s.controlVisible));
  const welcome = useUi((s) => s.welcome);
  const view = useUi((s) => s.view);
  const surface = mapSurfaceVisible({ welcome, view });

  // Touche N : seulement quand la carte est la surface visible et qu'elle a des lignes de la ZO.
  useEffect(() => {
    if (!has || !surface) return;
    const onKey = (e: KeyboardEvent) => {
      if (!isNamesKey(e, useUi.getState().draft !== null)) return;
      e.preventDefault();
      toggleMeasureNames();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [has, surface]);

  // Bouton bascule : nom stable (l'état passe par `aria-pressed`), l'infobulle dit l'action.
  const action = shown ? t("Masquer les noms des lignes et des zones") : t("Afficher les noms des lignes et des zones");
  return (
    <IconButton
      size={size}
      active={shown}
      aria-pressed={shown}
      aria-keyshortcuts="N"
      aria-label={t("Noms des lignes et des zones")}
      title={t("{action} (N)", { action })}
      onClick={toggleMeasureNames}
    >
      <Tags size={14} />
    </IconButton>
  );
}
